import { type CollectionConfig, CollectionSlug } from './collections.types';
import { starWarsCollection } from './collections/starWars.collection';
import { twentiethCenturyDesignCollection } from './collections/twentiethCenturyDesign.collection';

/**
 * Every collection page the site serves. A slug outside this map is a 404 -- see
 * `isUnknownCollectionSoft404`. Keys must be lowercase; `getCollectionConfig` lowercases the
 * path before looking them up.
 */
export const COLLECTIONS: Record<CollectionSlug, CollectionConfig> = {
    [CollectionSlug.TwentiethCenturyDesign]: twentiethCenturyDesignCollection,
    [CollectionSlug.StarWars]: starWarsCollection,
};

/**
 * Resolve `/collections/<slug>` to its config. Anchored at the start of the path on purpose:
 * `getIsCollectionsPage` is a substring test, and this is the allowlist that decides whether a
 * URL is a real collection page at all.
 *
 * Tolerates the `en-gb` basename because SSR hands `isSoft404Page` the basename-prefixed
 * location while react-router hands the client a stripped one -- same shape as
 * `isGuideArticlePage` in `@/utils/urls`.
 */
export const getCollectionConfig = (pathname: string = ''): CollectionConfig | undefined => {
    const normalized = pathname.split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase();
    const match = /^\/(?:en-gb\/)?collections\/([^/]+)$/.exec(normalized);
    if (!match) {
        return undefined;
    }
    const slug = match[1];
    /**
     * Own-property check, not a bare lookup: the slug comes from the URL, and a plain object
     * literal inherits from Object.prototype. `COLLECTIONS['constructor']` would otherwise return
     * the Object constructor -- truthy -- so /collections/constructor and /collections/__proto__
     * (the only two lowercase prototype keys, the rest die on the toLowerCase above) would be
     * treated as real collections and serve a broken 200 instead of a 404.
     */
    if (!Object.prototype.hasOwnProperty.call(COLLECTIONS, slug)) {
        return undefined;
    }
    // The own-property guard above proves this is a real slug, which the index signature cannot.
    return COLLECTIONS[slug as CollectionSlug];
};

export const isKnownCollectionPath = (pathname: string = ''): boolean => Boolean(getCollectionConfig(pathname));
